import { useEffect } from "react";
import { useAlert } from "../../../hooks/useAlert";

const AlertHeatmapComponent = ({ vmId }) => {
  const { getAlertHeatmap, heatmapData, loading, error } = useAlert();

  useEffect(() => {
    if (vmId !== undefined) {
      getAlertHeatmap(vmId);
    }
  }, [vmId]);

  const getColorByCount = (count, isFuture) => {
    if (isFuture) return "bg-gray-800 opacity-50";
    if (count === 0) return "bg-gray-700";
    if (count < 3) return "bg-green-500";
    if (count >= 3 && count <= 5) return "bg-orange-500";
    return "bg-red-500";
  };

  const formatDisplayDate = (date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${day}/${month}`;
  };

  const getTooltipText = (date, count, isFuture) => {
    const formattedDate = formatDisplayDate(date);
    if (isFuture) return `${formattedDate}: No data available`;
    return `${formattedDate}: ${count} alert${count !== 1 ? 's' : ''}`;
  };

  const isFutureDate = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date > today;
  };

  const processHeatmapData = (apiData) => {
    if (!apiData || apiData.length === 0) return [];

    const dateCountMap = new Map();
    apiData.forEach(item => {
      dateCountMap.set(item.date, item.count);
    });

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const todayDayOfWeek = today.getDay();
    const daysToMonday = todayDayOfWeek === 0 ? 6 : todayDayOfWeek - 1;
    
    const lastMonday = new Date(today);
    lastMonday.setDate(today.getDate() - daysToMonday);
    
    const startDate = new Date(lastMonday);
    startDate.setDate(lastMonday.getDate() - 21);
    
    const weeks = [];
    let currentWeek = [];
    
    for (let i = 0; i < 28; i++) {
      const currentDate = new Date(startDate);
      currentDate.setDate(startDate.getDate() + i);
      
      const year = currentDate.getFullYear();
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
      const day = String(currentDate.getDate()).padStart(2, '0');
      const dateString = `${year}-${month}-${day}`;
      
      const count = dateCountMap.get(dateString) || 0;
      const future = isFutureDate(currentDate);
      
      currentWeek.push({
        date: currentDate,
        dateString: dateString,
        count: count,
        isFuture: future
      });
      
      if (currentWeek.length === 7) {
        weeks.push([...currentWeek]);
        currentWeek = [];
      }
    }
    
    return weeks;
  };

  if (loading) {
    return (
      <div className="bg-primary-dark rounded-xl border border-gray-700 p-4 sm:p-6 h-full">
        <div className="text-center py-6 sm:py-8">
          <div className="inline-block w-6 h-6 sm:w-8 sm:h-8 border-4 border-primary-cyan border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-400 text-sm sm:text-base mt-2 sm:mt-3">Loading heatmap...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-primary-dark rounded-xl border border-gray-700 p-4 sm:p-6 h-full">
        <div className="text-center py-6 sm:py-8">
          <p className="text-red-400 text-sm sm:text-base">{error}</p>
        </div>
      </div>
    );
  }

  if (!heatmapData || !heatmapData.heatmapData || heatmapData.heatmapData.length === 0) {
    return (
      <div className="bg-primary-dark rounded-xl border border-gray-700 p-4 sm:p-6 h-full">
        <div className="text-center py-6 sm:py-8">
          <p className="text-gray-400 text-sm sm:text-base">Select a VM to view heatmap</p>
        </div>
      </div>
    );
  }

  const weeks = processHeatmapData(heatmapData.heatmapData);
  const weekDays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  
  const firstWeek = weeks[0];
  const lastWeek = weeks[3];
  const firstDate = firstWeek ? firstWeek[0].date : null;
  const lastDate = lastWeek ? lastWeek[6].date : null;
  
  const formatRange = (start, end) => {
    if (!start || !end) return "Last 28 days";
    const startStr = formatDisplayDate(start);
    const endStr = formatDisplayDate(end);
    return `${startStr} - ${endStr}`;
  };

  return (
    <div className="bg-primary-dark rounded-xl border border-gray-700 p-4 sm:p-6 h-full">
      <div className="flex flex-wrap justify-between items-center gap-2 mb-4 pb-3 border-b border-gray-700">
        <h3 className="text-base sm:text-lg font-semibold text-white">Alert Heatmap</h3>
        <span className="text-xs sm:text-sm text-gray-400">{formatRange(firstDate, lastDate)}</span>
      </div>

      <div className="grid grid-cols-7 gap-1 sm:gap-2 mb-2">
        {weekDays.map(day => (
          <div key={day} className="text-center text-gray-400 text-[10px] sm:text-xs font-medium">
            {day}
          </div>
        ))}
      </div>

      <div className="space-y-1 sm:space-y-2">
        {weeks.map((week, weekIndex) => (
          <div key={weekIndex} className="grid grid-cols-7 gap-1 sm:gap-2">
            {week.map((day, dayIndex) => (
              <div
                key={dayIndex}
                className={`aspect-square rounded-lg ${getColorByCount(day.count, day.isFuture)} ${!day.isFuture ? 'cursor-pointer transition-transform hover:scale-105' : 'cursor-not-allowed'}`}
                title={getTooltipText(day.date, day.count, day.isFuture)}
              >
                {!day.isFuture && (
                  <div className="relative w-full h-full group">
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/50 rounded-lg">
                      <span className="text-white text-[10px] sm:text-xs font-bold">{day.count}</span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>

      <div className="mt-4 sm:mt-6 pt-3 border-t border-gray-700">
        <div className="flex flex-wrap justify-center gap-2 sm:gap-4">
          <div className="flex items-center gap-1 sm:gap-2">
            <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-green-500"></div>
            <span className="text-gray-400 text-[10px] sm:text-xs">&lt;3 alerts</span>
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-orange-500"></div>
            <span className="text-gray-400 text-[10px] sm:text-xs">3-5 alerts</span>
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-red-500"></div>
            <span className="text-gray-400 text-[10px] sm:text-xs">&gt;5 alerts</span>
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-gray-800 opacity-50"></div>
            <span className="text-gray-400 text-[10px] sm:text-xs">Future</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlertHeatmapComponent;